import { Instagram, Facebook, Youtube, Mail, MapPin } from 'lucide-react';
import { formatPhone } from '@/lib/utils';

import type { Producto, Embajador } from '@/types';

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

type Page = 'inicio' | 'embajador' | 'catalogo' | 'tratamientos';

interface FooterProps {
  onNavigate: (page: Page, params?: { linea?: string; producto?: Producto }) => void;
  ambassador: Embajador | null;
}

const navItems = [
  { label: 'Inicio', path: 'inicio' as Page },
  { label: 'Embajador', path: 'embajador' as Page },
  { label: 'Catálogo', path: 'catalogo' as Page },
  { label: 'Tratamientos', path: 'tratamientos' as Page }
];

export default function Footer({ onNavigate, ambassador }: FooterProps) {
  const socialLinks = [
    { icon: Instagram, href: ambassador?.instagram_url, label: 'Instagram' },
    { icon: Facebook, href: ambassador?.facebook_url, label: 'Facebook' },
    { icon: Youtube, href: ambassador?.youtube_url, label: 'YouTube' },
  ].filter(social => social.href);

  return (
    <footer className="w-full bg-gradient-to-tr from-custom-green-light to-custom-green-dark text-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img src="/images/Logo_Volando_Alto_Footer.webp" alt="Volando Alto Logo" width={160} height={59} className="w-40 h-auto mb-4" />
            <p className="font-['Montserrat'] text-sm leading-relaxed mb-6">
              Belleza alcalina, ciencia pura. Transformamos vidas a través de 
              productos naturales respaldados por tecnología alcalinicéutica.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <social.icon className="w-5 h-5 text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-['Montserrat'] font-semibold mb-6 text-white text-base">Navegación</h3>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => onNavigate(item.path)}
                    className="font-['Montserrat'] hover:text-white/70 transition-colors text-sm"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Lines */}
          <div>
            <h3 className="font-['Montserrat'] font-semibold mb-6 text-white text-base">Líneas de Productos</h3>
            <ul className="space-y-3">
              {['FT-ELYM LINE', 'BIENESTAR LINE', 'AVANCELY LINE', 'VYBES LINE'].map((line) => (
                <li key={line}>
                  <button
                    onClick={() => onNavigate('catalogo', { linea: line })}
                    className="font-['Montserrat'] hover:text-white/70 transition-colors text-sm text-left"
                  >
                    {line}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-['Montserrat'] font-semibold mb-6 text-white text-base">Contacto</h3>
            <ul className="space-y-4">
              {ambassador?.whatsapp_number && (
                <li>
                  <a
                    href={`https://wa.me/${ambassador.whatsapp_number.toString().replace(/\D/g, '')}?text=${encodeURIComponent(
                      `Hola${ambassador.nombre_completo ? ` ${ambassador.nombre_completo}` : ''}, me comunico desde la plataforma de Volando Alto`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-['Montserrat'] flex items-center gap-3 hover:text-white/70 transition-colors text-sm"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    {formatPhone(ambassador.whatsapp_number)}
                  </a>
                </li>
              )}
              {ambassador?.email && (
                <li>
                  <a
                    href={`mailto:${ambassador.email}`}
                    className="font-['Montserrat'] flex items-center gap-3 hover:text-white/70 transition-colors text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    {ambassador.email}
                  </a>
                </li>
              )}
              {ambassador?.puntos_venta && ambassador.puntos_venta.length > 0 && (
                <li className="font-['Montserrat'] flex items-start gap-3 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {Array.from(new Set(ambassador.puntos_venta.map(p => p.ciudad))).join(' / ')}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/20 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-['Montserrat'] text-sm">
            © 2026 Volando Alto. Todos los derechos reservados.
          </p>
          <p className="font-['Montserrat'] text-sm flex items-center gap-1">
            {/*Hecho con <Heart className="w-4 h-4 text-[#e91e8c]" fill="currentColor" /> para transformar vidas*/}
          </p>
        </div>
      </div>
    </footer>
  );
}
